import {
  Button,
  Icon,
  Flag,
  Popover,
  Text,
  WithPopover,
} from '@lightspeed/design-system-react'
import type { PopOverDirections } from '@lightspeed/design-system-react'
import { T, useT } from '@transifex/react'
import React, { FunctionComponent, ReactElement, ReactNode } from 'react'

export interface SsboGuidedTourTooltipProps {
  /** The anchor element (button) the pop-up tethers to. */
  children: ReactElement
  /** Translated body copy for this step. */
  body: ReactNode
  /** 1-based step number for the "N of M" counter. */
  currentStep: number
  /** Total number of steps. */
  totalSteps: number
  /** Close (X) handler - ends the tour. */
  onDismiss: () => void
  /** Which side of the anchor the pop-up sits. */
  direction: PopOverDirections
  /** Toggles the pop-up while keeping the anchor mounted. */
  visible: boolean
}

export const SsboGuidedTourTooltip: FunctionComponent<SsboGuidedTourTooltipProps> = ({
  children,
  body,
  currentStep,
  totalSteps,
  onDismiss,
  direction,
  visible,
}) => {
  const t = useT()

  return (
    <WithPopover direction={direction}>
      {children}
      {visible ? (
        <Popover
          className="vd-popover--tooltip wr-tooltip"
          // Outside-click/Escape intentionally does nothing - only the X ends the tour.
          onClose={() => {}}
        >
          <div className="vd-flex vd-flex--justify-between vd-flex--align-center vd-mb6">
            <Flag
              className="wr-ssbo-guided-tour--counter"
              content={
                <T
                  _str="{currentStep} of {totalSteps}"
                  _context="SSBO guided tour step counter"
                  currentStep={currentStep}
                  totalSteps={totalSteps}
                />
              }
            />
            <Button
              modifier="icon"
              variant="supplementary"
              className="wr-ssbo-guided-tour--close"
              // Modal focus traps cancel `click` on this pop-up (tether portals
              // it outside the modal DOM) but not `pointerup`, which fires on
              // release like a click. onClick handles keyboard activation only
              // (detail is 0), so pointer interactions dismiss exactly once.
              onPointerUp={onDismiss}
              onClick={event => {
                if (event.detail === 0) {
                  onDismiss()
                }
              }}
              aria-label={t('Close', { _context: 'SSBO guided tour' })}
            >
              <Icon icon="times" vend />
            </Button>
          </div>
          <Text tag="p">{body}</Text>
        </Popover>
      ) : null}
    </WithPopover>
  )
}
